package com.routexk.quiz.pages

import com.routexk.quiz.ui.utils.rpx

import androidx.compose.runtime.*
import com.routexk.quiz.analytics.FbPixel
import com.routexk.quiz.analytics.Ga4
import com.routexk.quiz.di.LocalAppContainer
import com.routexk.quiz.funnel.FunnelApi
import com.routexk.quiz.funnel.FunnelCopy
import com.routexk.quiz.funnel.FunnelStore
import com.routexk.quiz.funnel.ui.FunnelLayout
import com.routexk.quiz.ui.theme.AppColors
import com.routexk.quiz.ui.theme.AppTypography
import com.routexk.quiz.ui.theme.withAlpha
import com.routexk.quiz.ui.utils.Constants
import com.routexk.quiz.ui.utils.isMobile
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color.Companion.rgb
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Img

private val GREEN = rgb(0x19, 0xB3, 0x5B)
private val RED = rgb(0xD5, 0x30, 0x30)

@Page("/success")
@Composable
fun SuccessPage() {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val mobile = isMobile()

    // Per-session AppsFlyer deep link: opens the app already connected to the
    // just-created account + subscription (via deep_link_sub2=<session_uuid>).
    var deepLink by remember { mutableStateOf<String?>(null) }
    var deepLinkLoaded by remember { mutableStateOf(false) }
    // QR encodes that deep link. Null while it's being built so we never flash
    // the generic fallback image first — it's only used if generation truly fails.
    var qrSrc by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        val plan = FunnelStore.selectedPlan
        val gaPlanId = plan?.let { Ga4.planId(it.trialDays) }
        // Wallet payments return here via redirect with a status query param
        val redirectStatus = ctx.route.params["redirect_status"]
        if (redirectStatus == "succeeded") {
            FunnelStore.paid = true
            // The wallet redirect reloaded the page, so checkout couldn't
            // track the purchase in place — fire it here instead
            val transactionId = ctx.route.params["payment_intent"]
            FunnelStore.lastTransactionId = transactionId
            Ga4.event(
                "purchase",
                "transaction_id" to transactionId,
                "value" to plan?.amountCents?.let { it / 100.0 },
                "currency" to "USD",
                "plan_id" to gaPlanId,
                "payment_method" to (FunnelStore.lastPaymentMethod ?: "card"),
                "coupon" to Ga4.COUPON,
                "items" to plan?.let {
                    Ga4.itemsArray(listOf(Ga4.planItem(gaPlanId!!, it.label, it.amountCents)))
                },
            )
            FunnelApi.trackEvent(container.defaultHttpClient, "purchase_completed")
        }
        // Only paid users see this screen. A failed/pending wallet redirect
        // (redirect_status != "succeeded") must NOT show "plan active" — send
        // them back to checkout to retry; unpaid direct visits go to the plan.
        if (!FunnelStore.paid) {
            ctx.router.navigateTo(if (redirectStatus != null) "/checkout?payment=failed" else "/plan")
            return@LaunchedEffect
        }
        Ga4.screenView(
            "success", 9,
            "transaction_id" to FunnelStore.lastTransactionId,
            "plan_id" to gaPlanId,
        )
        FbPixel.pageView()
        FbPixel.track("ViewContent", params = arrayOf("content_name" to "success"))
        // FB Purchase/StartTrial are keyed to the transaction, not to the page
        // render: refresh / back-button revisits must not create new purchases.
        // event_ids derive from the PaymentIntent id, so the CAPI copies sent
        // by the Stripe webhook deduplicate with these browser events.
        val txId = FunnelStore.lastTransactionId
        if (txId != null && plan != null && gaPlanId != null && !FbPixel.purchaseAlreadySent(txId)) {
            val amount = plan.amountCents / 100.0
            FbPixel.track(
                "Purchase",
                eventId = FbPixel.purchaseEventId(txId),
                "content_ids" to FbPixel.ids(gaPlanId),
                "content_type" to "product",
                "value" to amount,
                "currency" to "USD",
            )
            FbPixel.track(
                "StartTrial",
                eventId = FbPixel.trialEventId(txId),
                "value" to amount,
                "currency" to "USD",
                // Renewal price after the paid trial ($49.99/mo)
                "predicted_ltv" to plan.renewalCents / 100.0,
            )
            FbPixel.markPurchaseSent(txId)
        }
        deepLink = FunnelApi.fetchDeepLink(container.defaultHttpClient, FunnelStore.intentUuid)
        deepLinkLoaded = true
    }

    // Render the QR client-side once the deep link is known (no session id ever
    // leaves the browser). Only drop to the static image if we genuinely can't
    // build the personalized one, so the default QR never flashes first.
    LaunchedEffect(deepLink, deepLinkLoaded) {
        if (!deepLinkLoaded) return@LaunchedEffect
        val dl = deepLink
        if (dl == null) {
            qrSrc = "/funnel/qr-code.svg"
            return@LaunchedEffect
        }
        // The generator script may still be loading; retry briefly before giving up.
        repeat(20) {
            qrDataUrl(dl)?.let { qrSrc = it; return@LaunchedEffect }
            kotlinx.coroutines.delay(150)
        }
        qrSrc = "/funnel/qr-code.svg"
    }

    FunnelLayout(
        title = com.routexk.quiz.i18n.t("RouteXK — Plan Active", "RouteXK — Plan activo"),
        // Warm background on desktop only; mobile stays plain
        warm = !mobile,
        maxContentWidth = 1200.rpx
    ) {
        Row(
            Modifier.fillMaxWidth().gap(40.rpx).flexWrap(FlexWrap.Wrap).margin(top = 16.rpx),
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(
                Modifier.maxWidth(560.rpx).gap(16.rpx),
                // Mobile centers everything; desktop stays left-aligned per design
                horizontalAlignment = if (mobile) Alignment.CenterHorizontally else Alignment.Start
            ) {
                // Check circle + status pill, side by side (design: 36px circle, 12px gap)
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.gap(12.rpx)
                ) {
                    Box(
                        Modifier
                            .size(36.rpx)
                            .flexShrink(0)
                            .borderRadius(50.percent)
                            .styleModifier {
                                property("background", "linear-gradient(135deg, #19B35B 0%, #0FA04E 100%)")
                                property("box-shadow", "0 4px 9px rgba(25, 179, 91, 0.38)")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        Img(
                            src = "/funnel/success-check.svg",
                            attrs = { attr("aria-hidden", "true"); style { width(18.rpx); height(18.rpx) } }
                        )
                    }
                    SpanText(
                        if (mobile) FunnelCopy.SUCCESS_BADGE_MOBILE else FunnelCopy.SUCCESS_BADGE,
                        Modifier
                            .padding(topBottom = 8.rpx, leftRight = 16.rpx)
                            .borderRadius(100.rpx)
                            .backgroundColor(GREEN.withAlpha(0.1f))
                            .border(1.rpx, LineStyle.Solid, rgb(0xD1, 0xF0, 0xDE))
                            .fontSize(13.rpx)
                            .fontWeight(FontWeight.SemiBold)
                            .fontFamily(AppTypography.TERTIARY_FONT_NAME, AppTypography.TERTIARY_FONT_FALLBACK)
                            .color(GREEN)
                    )
                }

                Column(
                    Modifier.fillMaxWidth().gap(8.rpx).margin(bottom = 8.rpx),
                    horizontalAlignment = if (mobile) Alignment.CenterHorizontally else Alignment.Start
                ) {
                    val titleAlign = if (mobile) com.varabyte.kobweb.compose.css.TextAlign.Center
                        else com.varabyte.kobweb.compose.css.TextAlign.Start
                    SpanText(
                        if (mobile) FunnelCopy.SUCCESS_TITLE_DARK_MOBILE else FunnelCopy.SUCCESS_TITLE_DARK,
                        Modifier
                            .fillMaxWidth()
                            .fontSize(if (mobile) 30.rpx else 40.rpx)
                            .lineHeight(44.rpx)
                            .fontWeight(800)
                            .textAlign(titleAlign)
                            .fontFamily(AppTypography.TERTIARY_FONT_NAME, AppTypography.TERTIARY_FONT_FALLBACK)
                            .color(rgb(0x1E, 0x1D, 0x1D))
                    )
                    SpanText(
                        FunnelCopy.SUCCESS_TITLE_ACCENT,
                        Modifier
                            .fillMaxWidth()
                            .fontSize(if (mobile) 30.rpx else 40.rpx)
                            .lineHeight(44.rpx)
                            .fontWeight(800)
                            .textAlign(titleAlign)
                            .fontFamily(AppTypography.TERTIARY_FONT_NAME, AppTypography.TERTIARY_FONT_FALLBACK)
                            .color(RED)
                    )
                }

                // QR / steps card
                Box(
                    Modifier
                        .fillMaxWidth()
                        .backgroundColor(Colors.White)
                        .border(1.rpx, LineStyle.Solid, rgb(0xEB, 0xEB, 0xEB))
                        .borderRadius(40.rpx)
                        .padding(24.rpx)
                        .styleModifier { property("box-shadow", "0 6px 12px rgba(0,0,0,0.04)") }
                ) {
                    Column(Modifier.fillMaxWidth().gap(24.rpx)) {
                        val qr: @Composable () -> Unit = {
                            Box(
                                Modifier.size(240.rpx).flexShrink(0).position(Position.Relative),
                                contentAlignment = Alignment.Center
                            ) {
                                val src = qrSrc
                                if (src != null) {
                                    Img(
                                        src = src,
                                        attrs = {
                                            attr("alt", "QR code to open the RouteXK app with your subscription")
                                            style { width(202.rpx); height(202.rpx) }
                                        }
                                    )
                                } else {
                                    // Building the personalized code — placeholder
                                    // keeps the frame stable, no default-QR flash.
                                    Box(
                                        Modifier.size(202.rpx),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        com.routexk.quiz.ui.components.widgets.LoadingSpinner(size = 32.rpx)
                                    }
                                }
                                QrBracket(top = true, left = true)
                                QrBracket(top = true, left = false)
                                QrBracket(top = false, left = true)
                                QrBracket(top = false, left = false)
                            }
                        }
                        val steps: @Composable () -> Unit = {
                            Column(Modifier.fillMaxWidth().gap(12.rpx)) {
                                listOf(
                                    FunnelCopy.SUCCESS_STEP_1,
                                    FunnelCopy.SUCCESS_STEP_2,
                                    FunnelCopy.SUCCESS_STEP_3
                                ).forEachIndexed { index, step ->
                                    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(12.rpx)) {
                                        Box(
                                            Modifier
                                                .size(24.rpx)
                                                .flexShrink(0)
                                                .borderRadius(50.percent)
                                                .backgroundColor(AppColors.Funnel.PageBackground)
                                                .border(1.rpx, LineStyle.Solid, rgb(0xE8, 0xE8, 0xED)),
                                            contentAlignment = Alignment.Center
                                        ) {
                                            SpanText(
                                                "${index + 1}",
                                                Modifier.fontSize(14.rpx).fontWeight(FontWeight.SemiBold)
                                                    .fontFamily(AppTypography.TERTIARY_FONT_NAME, AppTypography.TERTIARY_FONT_FALLBACK)
                                                    .color(AppColors.Funnel.TextDark)
                                            )
                                        }
                                        SpanText(
                                            step,
                                            Modifier.fontSize(16.rpx).lineHeight(24.rpx).fontWeight(FontWeight.SemiBold)
                                                .fontFamily(AppTypography.TERTIARY_FONT_NAME, AppTypography.TERTIARY_FONT_FALLBACK)
                                                .color(rgb(0x12, 0x18, 0x1C))
                                        )
                                    }
                                }
                            }
                        }

                        if (mobile) {
                            // Mobile: no QR, steps + red CTA
                            steps()
                            A(
                                // Deep link opens the app connected to the paid
                                // account; store link is the fallback. No href
                                // until the fetch settles so an early tap can't
                                // land on the store instead of the deep link.
                                // Same-tab navigation on purpose: iOS Universal
                                // Links / Android App Links don't fire from
                                // target="_blank", which left mobile users on
                                // the OneLink fallback page instead of the app.
                                href = if (deepLinkLoaded) deepLink ?: Constants.PLAY_STORE_URL else null,
                                attrs = {
                                    attr("aria-label", FunnelCopy.SUCCESS_GET_APP)
                                    style { property("text-decoration", "none"); width(100.percent) }
                                }
                            ) {
                                com.routexk.quiz.funnel.ui.FunnelCta(
                                    text = FunnelCopy.SUCCESS_GET_APP,
                                    loading = !deepLinkLoaded,
                                    onClick = {
                                        val ua = kotlinx.browser.window.navigator.userAgent
                                        val platform = if (ua.contains("Android", ignoreCase = true)) "android" else "ios"
                                        Ga4.event("get_the_app_clicked", "platform" to platform)
                                        FbPixel.trackCustom("AppDownloadClick", "platform" to platform)
                                    },
                                    modifier = Modifier.fillMaxWidth(),
                                    trailingIcon = "/funnel/external-link.svg"
                                )
                            }
                        } else {
                            // Desktop: QR left, steps right (design)
                            Row(
                                Modifier.fillMaxWidth().gap(16.rpx),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                qr()
                                steps()
                            }
                        }

                        // Store row: Google Play · or · App Store, flanked by hairlines
                        Row(
                            Modifier.fillMaxWidth().gap(12.rpx),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.Center
                        ) {
                            if (!mobile) Box(Modifier.width(63.rpx).height(1.rpx).backgroundColor(rgb(0xE8, 0xE8, 0xED)))
                            StoreBadge("Google Play", "/funnel/store-google-play-icon.svg", Constants.PLAY_STORE_URL, platform = "android")
                            SpanText("or", Modifier.fontSize(12.rpx).color(Colors.Black.withAlpha(0.6f)))
                            StoreBadge("App Store", "/funnel/store-apple-icon.svg", Constants.APP_STORE_URL, platform = "ios")
                            if (!mobile) Box(Modifier.width(63.rpx).height(1.rpx).backgroundColor(rgb(0xE8, 0xE8, 0xED)))
                        }
                    }
                }
            }

            if (!mobile) {
                Img(
                    src = "/funnel/phones.png",
                    attrs = {
                        attr("alt", "RouteXK app showing an optimized route")
                        style {
                            property("flex", "1 1 0")
                            minWidth(548.rpx)
                            maxWidth(680.rpx)
                            width(100.percent)
                        }
                    }
                )
            }
        }
    }
}

/**
 * Builds a QR-code data URL for [text] using the qrcode-generator library
 * loaded in the page head. Runs entirely in the browser; returns null if the
 * library is unavailable so the caller can keep the static fallback image.
 */
private fun qrDataUrl(text: String): String? =
    try {
        val q = js("qrcode(0, 'M')")
        q.addData(text)
        q.make()
        q.createDataURL(6, 8) as? String
    } catch (e: Throwable) {
        null
    }

@Composable
private fun QrBracket(top: Boolean, left: Boolean) {
    Box(
        Modifier
            .size(25.rpx)
            .position(Position.Absolute)
            .then(if (top) Modifier.top(0.rpx) else Modifier.bottom(0.rpx))
            .then(if (left) Modifier.left(0.rpx) else Modifier.right(0.rpx))
            .styleModifier {
                val color = "#D53030"
                if (top) property("border-top", "3px solid $color")
                else property("border-bottom", "3px solid $color")
                if (left) property("border-left", "3px solid $color")
                else property("border-right", "3px solid $color")
                when {
                    top && left -> property("border-top-left-radius", "8px")
                    top && !left -> property("border-top-right-radius", "8px")
                    !top && left -> property("border-bottom-left-radius", "8px")
                    else -> property("border-bottom-right-radius", "8px")
                }
            }
    )
}

@Composable
private fun StoreBadge(label: String, icon: String, url: String, platform: String) {
    A(
        href = url,
        attrs = {
            attr("target", "_blank")
            attr("rel", "noopener noreferrer")
            attr("aria-label", "Download on $label")
            style { property("text-decoration", "none") }
            onClick {
                Ga4.event("get_the_app_clicked", "platform" to platform)
                FbPixel.trackCustom("AppDownloadClick", "platform" to platform)
            }
        }
    ) {
        Row(
            Modifier
                .padding(topBottom = 6.rpx, leftRight = 4.rpx)
                .gap(8.rpx),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Img(
                src = icon,
                attrs = { attr("aria-hidden", "true"); style { width(19.rpx); height(22.rpx) } }
            )
            SpanText(
                label,
                Modifier
                    .fontSize(18.rpx)
                    .fontWeight(FontWeight.Medium)
                    .letterSpacing((-0.47).rpx)
                    .color(rgb(0x4D, 0x4D, 0x4D))
            )
        }
    }
}
